/**
 * Page Stocks Atelier (Phase 2)
 * Gestion des consommables (carburant, graisse, boulons, filtres) avec mouvements.
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertTriangle, Plus, Edit, Trash2, ArrowDownCircle, ArrowUpCircle, Package, Fuel, Wrench, Cog, Droplet } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORIES = [
  { value: 'carburant', label: 'Carburant', icon: Fuel, color: 'text-orange-500' },
  { value: 'huile', label: 'Huile', icon: Droplet, color: 'text-amber-500' },
  { value: 'ad_blue', label: 'AdBlue', icon: Droplet, color: 'text-blue-500' },
  { value: 'graisse', label: 'Graisse', icon: Cog, color: 'text-yellow-700' },
  { value: 'boulons', label: 'Boulons / Visserie', icon: Wrench, color: 'text-gray-500' },
  { value: 'filtres', label: 'Filtres', icon: Package, color: 'text-purple-500' },
  { value: 'lubrifiant', label: 'Lubrifiant', icon: Droplet, color: 'text-green-500' },
  { value: 'piece_usure', label: 'Pièce d\'usure', icon: Wrench, color: 'text-red-500' },
  { value: 'autre', label: 'Autre', icon: Package, color: 'text-gray-400' },
];

export default function StocksAtelierPage() {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [showMvt, setShowMvt] = useState(null); // stock_id à charger
  const [editing, setEditing] = useState(null);
  const [mvtType, setMvtType] = useState('sortie');
  const [mvtQte, setMvtQte] = useState(0);
  const [mvtMotif, setMvtMotif] = useState('');
  const [newStock, setNewStock] = useState({ nom: '', categorie: 'carburant', unite: 'L', quantite_stock: 0, seuil_alerte: 0, prix_unitaire: 0 });

  const fetchStocks = async () => {
    setLoading(true);
    try {
      const r = await api.get('/stocks-atelier' + (filter !== 'all' ? `?categorie=${filter}` : ''));
      setStocks(r.data?.stocks || []);
    } catch { toast.error('Erreur chargement stocks'); }
    setLoading(false);
  };
  useEffect(() => { fetchStocks(); }, [filter]);

  const handleCreate = async () => {
    if (!newStock.nom) { toast.error('Nom requis'); return; }
    try {
      await api.post('/stocks-atelier', newStock);
      toast.success('Stock créé');
      setShowAdd(false);
      setNewStock({ nom: '', categorie: 'carburant', unite: 'L', quantite_stock: 0, seuil_alerte: 0, prix_unitaire: 0 });
      fetchStocks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/stocks-atelier/${editing.id}`, editing);
      toast.success('Stock mis à jour');
      setEditing(null);
      fetchStocks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce stock ?')) return;
    try {
      await api.delete(`/stocks-atelier/${id}`);
      toast.success('Stock supprimé');
      fetchStocks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleMvt = async () => {
    try {
      await api.post('/stocks-atelier/mouvements', {
        stock_id: showMvt.id,
        type: mvtType,
        quantite: parseFloat(mvtQte),
        motif: mvtMotif,
      });
      toast.success(`${mvtType === 'entree' ? 'Entrée' : mvtType === 'sortie' ? 'Sortie' : 'Ajustement'} enregistré(e)`);
      setShowMvt(null);
      setMvtQte(0);
      setMvtMotif('');
      fetchStocks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const enAlerte = stocks.filter(s => s.en_alerte);

  return (
    <div className="space-y-4" data-testid="stocks-atelier-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="w-7 h-7 text-primary" />
            Stocks Atelier
          </h1>
          <p className="text-muted-foreground">Carburants, graisses, boulons, consommables</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="btn-add-stock">
          <Plus className="w-4 h-4 mr-2" />Ajouter un stock
        </Button>
      </div>

      {enAlerte.length > 0 && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-950/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-800 dark:text-red-200">{enAlerte.length} stock(s) en alerte</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {enAlerte.map(s => (
                    <Badge key={s.id} variant="destructive">{s.nom} : {s.quantite_stock} {s.unite}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')}>Toutes ({stocks.length})</Button>
        {CATEGORIES.map(c => {
          const count = stocks.filter(s => s.categorie === c.value).length;
          if (count === 0 && filter !== c.value) return null;
          return <Button key={c.value} size="sm" variant={filter === c.value ? 'default' : 'outline'} onClick={() => setFilter(c.value)}>{c.label} ({count})</Button>;
        })}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <p className="p-6 text-center text-muted-foreground">Chargement...</p> :
           stocks.length === 0 ? (
            <p className="p-6 text-center text-muted-foreground">Aucun stock — cliquez sur "Ajouter un stock"</p>
           ) : (
            <div className="divide-y" data-testid="stocks-list">
              {stocks.map(s => {
                const cat = CATEGORIES.find(c => c.value === s.categorie) || CATEGORIES[CATEGORIES.length - 1];
                const Icon = cat.icon;
                return (
                  <div key={s.id} className="p-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Icon className={`w-5 h-5 ${cat.color} flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{s.nom}</p>
                        <p className="text-xs text-muted-foreground">{cat.label} {s.emplacement ? `· ${s.emplacement}` : ''}{s.fournisseur ? ` · ${s.fournisseur}` : ''}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-lg ${s.en_alerte ? 'text-red-500' : ''}`}>
                        {s.quantite_stock} <span className="text-sm font-normal">{s.unite}</span>
                      </p>
                      {s.seuil_alerte > 0 && <p className="text-xs text-muted-foreground">seuil: {s.seuil_alerte}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => { setShowMvt(s); setMvtType('entree'); }} title="Entrée stock">
                        <ArrowUpCircle className="w-4 h-4 text-green-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => { setShowMvt(s); setMvtType('sortie'); }} title="Sortie stock">
                        <ArrowDownCircle className="w-4 h-4 text-orange-500" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setEditing({ ...s })}><Edit className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
           )}
        </CardContent>
      </Card>

      {/* Dialog création */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau stock consommable</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom *</Label><Input value={newStock.nom} onChange={e => setNewStock({ ...newStock, nom: e.target.value })} placeholder="Ex: GNR Gazole Non Routier" data-testid="new-stock-nom" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Catégorie</Label>
                <Select value={newStock.categorie} onValueChange={v => setNewStock({ ...newStock, categorie: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Unité</Label>
                <Select value={newStock.unite} onValueChange={v => setNewStock({ ...newStock, unite: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['L', 'kg', 'g', 'unité', 'boîte', 'rouleau'].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Stock actuel</Label><Input type="number" step="0.1" value={newStock.quantite_stock} onChange={e => setNewStock({ ...newStock, quantite_stock: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Seuil alerte</Label><Input type="number" step="0.1" value={newStock.seuil_alerte} onChange={e => setNewStock({ ...newStock, seuil_alerte: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Prix unitaire</Label><Input type="number" step="0.01" value={newStock.prix_unitaire} onChange={e => setNewStock({ ...newStock, prix_unitaire: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Emplacement</Label><Input value={newStock.emplacement || ''} onChange={e => setNewStock({ ...newStock, emplacement: e.target.value })} placeholder="Ex: Atelier - étagère B3" /></div>
            <div><Label>Fournisseur</Label><Input value={newStock.fournisseur || ''} onChange={e => setNewStock({ ...newStock, fournisseur: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
            <Button onClick={handleCreate} data-testid="btn-save-stock">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Edit */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier {editing.nom}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom</Label><Input value={editing.nom} onChange={e => setEditing({ ...editing, nom: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Stock</Label><Input type="number" step="0.1" value={editing.quantite_stock} onChange={e => setEditing({ ...editing, quantite_stock: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Seuil alerte</Label><Input type="number" step="0.1" value={editing.seuil_alerte} onChange={e => setEditing({ ...editing, seuil_alerte: parseFloat(e.target.value) || 0 })} /></div>
                <div><Label>Prix unitaire</Label><Input type="number" step="0.01" value={editing.prix_unitaire || 0} onChange={e => setEditing({ ...editing, prix_unitaire: parseFloat(e.target.value) || 0 })} /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button onClick={handleUpdate}>Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog Mouvement */}
      {showMvt && (
        <Dialog open={!!showMvt} onOpenChange={(o) => !o && setShowMvt(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{mvtType === 'entree' ? '⬆️ Entrée' : mvtType === 'sortie' ? '⬇️ Sortie' : '🔧 Ajustement'} - {showMvt.nom}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-sm">Stock actuel : <strong>{showMvt.quantite_stock} {showMvt.unite}</strong></p>
              <div><Label>Quantité ({showMvt.unite})</Label><Input type="number" step="0.1" autoFocus value={mvtQte} onChange={e => setMvtQte(e.target.value)} data-testid="input-mvt-qte" /></div>
              <div><Label>Motif</Label><Input value={mvtMotif} onChange={e => setMvtMotif(e.target.value)} placeholder="Ex: Plein tracteur JD-6155R" /></div>
              {mvtType === 'sortie' && parseFloat(mvtQte) > showMvt.quantite_stock && (
                <p className="text-sm text-red-500">⚠️ Le stock deviendra négatif</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMvt(null)}>Annuler</Button>
              <Button onClick={handleMvt} data-testid="btn-confirm-mvt">Valider</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
